import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inssTablesService } from '../services/api';
import { Save, Plus, Trash2, AlertCircle, CheckCircle, Calendar, Edit2, X } from 'lucide-react';
import toast from 'react-hot-toast';

const InssTablesManagement = () => {
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [editingTable, setEditingTable] = useState(null);
  const [ranges, setRanges] = useState([]);
  const [newRange, setNewRange] = useState({ max: '', percentage: '', deduction: '' });
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({ max: '', percentage: '', deduction: '' });

  const { data: tablesData, isLoading } = useQuery({
    queryKey: ['inss-tables'],
    queryFn: () => inssTablesService.getInssTables(),
  });

  const { data: activeTableData } = useQuery({
    queryKey: ['inss-tables-active'],
    queryFn: () => inssTablesService.getInssTables({ active: 'true' }),
    enabled: true,
  });

  const upsertMutation = useMutation({
    mutationFn: (data) => inssTablesService.upsertInssTable(data),
    onSuccess: () => {
      toast.success('Tabela INSS salva com sucesso!');
      queryClient.invalidateQueries(['inss-tables']);
      queryClient.invalidateQueries(['inss-tables-active']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar tabela');
    },
  });

  const activateMutation = useMutation({
    mutationFn: (year) => inssTablesService.activateInssTable(year),
    onSuccess: () => {
      toast.success('Tabela ativada com sucesso!');
      queryClient.invalidateQueries(['inss-tables']);
      queryClient.invalidateQueries(['inss-tables-active']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao ativar tabela');
    },
  });

  const activeYear = activeTableData?.data?.data?.[0]?.year || null;

  const loadTableForYear = (year) => {
    const table = tablesData?.data?.data?.find(t => t.year === year);
    if (table) {
      setEditingTable(table);
      setRanges([...table.ranges].sort((a, b) => a.max - b.max));
    } else {
      setEditingTable(null);
      setRanges([]);
    }
  };

  useEffect(() => {
    if (selectedYear) {
      loadTableForYear(selectedYear);
    }
  }, [selectedYear, tablesData]);

  const handleAddRange = () => {
    if (!newRange.max || !newRange.percentage) {
      toast.error('Preencha o valor máximo e percentual');
      return;
    }
    const max = parseFloat(newRange.max.replace(',', '.'));
    const percentage = parseFloat(newRange.percentage.replace(',', '.'));
    const deduction = newRange.deduction ? parseFloat(newRange.deduction.replace(',', '.')) : 0;

    if (isNaN(max) || isNaN(percentage)) {
      toast.error('Valores inválidos');
      return;
    }

    setRanges([...ranges, { max, percentage, deduction }].sort((a, b) => a.max - b.max));
    setNewRange({ max: '', percentage: '', deduction: '' });
  };

  const handleRemoveRange = (index) => {
    setRanges(ranges.filter((_, i) => i !== index));
  };

  const handleEditRange = (index) => {
    setEditingIndex(index);
    setEditForm({
      max: String(ranges[index].max),
      percentage: String(ranges[index].percentage),
      deduction: String(ranges[index].deduction || ''),
    });
  };

  const handleSaveEdit = () => {
    if (!editForm.max || !editForm.percentage) {
      toast.error('Preencha o valor máximo e percentual');
      return;
    }
    const max = parseFloat(editForm.max.replace(',', '.'));
    const percentage = parseFloat(editForm.percentage.replace(',', '.'));
    const deduction = editForm.deduction ? parseFloat(editForm.deduction.replace(',', '.')) : 0;

    if (isNaN(max) || isNaN(percentage)) {
      toast.error('Valores inválidos');
      return;
    }

    const updatedRanges = [...ranges];
    updatedRanges[editingIndex] = { max, percentage, deduction };
    setRanges(updatedRanges.sort((a, b) => a.max - b.max));
    setEditingIndex(null);
    setEditForm({ max: '', percentage: '', deduction: '' });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm({ max: '', percentage: '', deduction: '' });
  };

  const handleSave = () => {
    if (ranges.length === 0) {
      toast.error('Adicione pelo menos uma faixa');
      return;
    }
    upsertMutation.mutate({
      year: selectedYear,
      ranges,
      is_active: true,
    });
  };

  const handleActivate = () => {
    activateMutation.mutate(selectedYear);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faixas INSS</h1>
          <p className="text-gray-600">
            Configure as faixas de contribuição INSS por ano. A tabela ativa é usada nos cálculos de holerite.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeYear && (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Tabela ativa: {activeYear}
            </span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4 mb-6">
          <label className="text-sm font-medium text-gray-700">Ano:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {[2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027, 2028].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          {selectedYear !== activeYear && editingTable && (
            <button
              onClick={handleActivate}
              disabled={activateMutation.isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {activateMutation.isLoading ? 'Ativando...' : 'Ativar este ano'}
            </button>
          )}
        </div>

        {isLoading ? (
          <p>Carregando...</p>
        ) : (
          <>
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Faixas de Desconto INSS - {selectedYear}</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">Até (R$)</th>
                    <th className="px-4 py-2 text-left text-gray-700">Percentual (%)</th>
                    <th className="px-4 py-2 text-left text-gray-700">Dedução (R$)</th>
                    <th className="px-4 py-2 text-left text-gray-700">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {ranges.map((range, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      {editingIndex === index ? (
                        <>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={editForm.max}
                              onChange={(e) => setEditForm({ ...editForm, max: e.target.value })}
                              className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={editForm.percentage}
                              onChange={(e) => setEditForm({ ...editForm, percentage: e.target.value })}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={editForm.deduction}
                              onChange={(e) => setEditForm({ ...editForm, deduction: e.target.value })}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Salvar"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-500 hover:text-gray-700 p-1"
                                title="Cancelar"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2">
                            {Number(range.max).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </td>
                          <td className="px-4 py-2">{range.percentage}%</td>
                          <td className="px-4 py-2">
                            {range.deduction ? Number(range.deduction).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEditRange(index)}
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRemoveRange(index)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Remover"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {ranges.length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-4 py-4 text-center text-gray-500">
                        Nenhuma faixa cadastrada. Adicione uma nova faixa abaixo.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Adicionar Nova Faixa</h4>
              <div className="flex items-end gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Valor Máximo (R$)</label>
                  <input
                    type="text"
                    value={newRange.max}
                    onChange={(e) => setNewRange({ ...newRange, max: e.target.value })}
                    className="w-40 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ex: 1518.00"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Percentual (%)</label>
                  <input
                    type="text"
                    value={newRange.percentage}
                    onChange={(e) => setNewRange({ ...newRange, percentage: e.target.value })}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ex: 7.5"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Dedução (R$)</label>
                  <input
                    type="text"
                    value={newRange.deduction}
                    onChange={(e) => setNewRange({ ...newRange, deduction: e.target.value })}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Opcional"
                  />
                </div>
                <button
                  onClick={handleAddRange}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setRanges([]);
                  setEditingTable(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Limpar
              </button>
              <button
                onClick={handleSave}
                disabled={upsertMutation.isLoading}
                className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {upsertMutation.isLoading ? 'Salvando...' : 'Salvar Tabela'}
              </button>
            </div>
          </>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Como funciona o cálculo progressivo:</p>
            <p className="mt-1">
              O desconto é aplicado por faixas. Cada faixa tem um teto máximo e um percentual. Se o salário
              ultrapassa o teto da faixa, o percentual é aplicado apenas sobre o valor que excede o teto anterior.
            </p>
            <p className="mt-2">
              A <strong>dedução</strong> é o valor fixo a descontar após aplicar o percentual, usado para
              manter o cálculo correto quando há salto de faixa.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InssTablesManagement;